import { createContext, useContext, useState, useEffect } from 'react';

const ThemeContext = createContext();

const THEME_KEY = 'nexi_theme';

export const themes = {
  dark: {
    name: 'dark',
    label: 'Dark Mode',
    colors: {
      background: '#0a0a0f',
      card: 'rgba(20, 20, 30, 0.8)',
      text: '#ffffff',
      textMuted: 'rgba(255, 255, 255, 0.7)',
      textFaded: 'rgba(255, 255, 255, 0.4)',
      border: 'rgba(255, 255, 255, 0.1)',
      primary: '#8b5cf6',
      secondary: '#06b6d4',
      accent: '#f43f5e',
    }
  },
  light: {
    name: 'light',
    label: 'Light Mode',
    colors: {
      background: '#f8fafc',
      card: 'rgba(255, 255, 255, 0.9)',
      text: '#1e293b',
      textMuted: 'rgba(30, 41, 59, 0.8)',
      textFaded: 'rgba(30, 41, 59, 0.5)',
      border: 'rgba(0, 0, 0, 0.1)',
      primary: '#7c3aed',
      secondary: '#0891b2',
      accent: '#e11d48',
    }
  },
  midnight: {
    name: 'midnight',
    label: 'Midnight Blue',
    colors: {
      background: '#0f172a',
      card: 'rgba(30, 41, 59, 0.8)',
      text: '#f1f5f9',
      textMuted: 'rgba(241, 245, 249, 0.8)',
      textFaded: 'rgba(241, 245, 249, 0.5)',
      border: 'rgba(148, 163, 184, 0.2)',
      primary: '#3b82f6',
      secondary: '#22d3ee',
      accent: '#f472b6',
    }
  },
  sunset: {
    name: 'sunset',
    label: 'Sunset Warm',
    colors: {
      background: '#1c1917',
      card: 'rgba(41, 37, 36, 0.8)',
      text: '#fef3c7',
      textMuted: 'rgba(254, 243, 199, 0.8)',
      textFaded: 'rgba(254, 243, 199, 0.5)',
      border: 'rgba(251, 191, 36, 0.2)',
      primary: '#f59e0b',
      secondary: '#ef4444',
      accent: '#ec4899',
    }
  }
};

export const ThemeProvider = ({ children }) => {
  const [currentTheme, setCurrentTheme] = useState('dark');

  useEffect(() => {
    // Load saved theme on mount
    const savedTheme = localStorage.getItem(THEME_KEY);
    if (savedTheme && themes[savedTheme]) {
      setCurrentTheme(savedTheme);
      applyTheme(savedTheme);
    } else {
      applyTheme('dark');
    }
  }, []);

  const applyTheme = (themeName) => {
    const theme = themes[themeName];
    if (!theme) return;

    const root = document.documentElement;
    
    // Apply CSS variables
    root.style.setProperty('--theme-background', theme.colors.background);
    root.style.setProperty('--theme-card', theme.colors.card);
    root.style.setProperty('--theme-text', theme.colors.text);
    root.style.setProperty('--theme-text-muted', theme.colors.textMuted);
    root.style.setProperty('--theme-text-faded', theme.colors.textFaded);
    root.style.setProperty('--theme-border', theme.colors.border);
    root.style.setProperty('--theme-primary', theme.colors.primary);
    root.style.setProperty('--theme-secondary', theme.colors.secondary);
    root.style.setProperty('--theme-accent', theme.colors.accent);

    // Set body class for Tailwind
    document.body.classList.remove('theme-dark', 'theme-light', 'theme-midnight', 'theme-sunset');
    document.body.classList.add(`theme-${themeName}`);
  };

  const changeTheme = (themeName) => {
    if (themes[themeName]) {
      setCurrentTheme(themeName);
      localStorage.setItem(THEME_KEY, themeName);
      applyTheme(themeName);
    }
  };

  const toggleTheme = () => {
    const themeNames = Object.keys(themes);
    const currentIndex = themeNames.indexOf(currentTheme);
    const nextIndex = (currentIndex + 1) % themeNames.length;
    changeTheme(themeNames[nextIndex]);
  };

  const value = {
    currentTheme,
    theme: themes[currentTheme],
    themes,
    changeTheme,
    toggleTheme,
    isDark: currentTheme === 'dark' || currentTheme === 'midnight' || currentTheme === 'sunset'
  };

  return (
    <ThemeContext.Provider value={value}>
      {children}
    </ThemeContext.Provider>
  );
};

export const useTheme = () => {
  const context = useContext(ThemeContext);
  if (!context) {
    throw new Error('useTheme must be used within a ThemeProvider');
  }
  return context;
};

export default ThemeContext;
